import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import i18n from 'modules/core/lib/i18n'
import type { ProductCatalogCharacteristicGroup } from 'modules/it_management/lib/productCatalogGraphql'
import { CATEGORY_LABELS } from 'modules/it_management/hooks/device_catalog/constants'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import type {
  AttributeFilters,
  DeviceCatalogView,
  DistributorProductVariant,
  FilterSection,
  ProductGroup,
} from './types'
import { CATEGORY_TYPE_MAP, getSavedIdsStorageKey } from './types'

/** Static category label lookup — avoids dynamic i18n keys */
function getCategoryI18nLabel(catId: string): string {
  return CATEGORY_LABELS[catId]?.() ?? catId
}

function loadSavedIds(storageKey: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistSavedIds(ids: string[], storageKey: string): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(ids))
  } catch {
    // Private browsing may throw
  }
}

type UseDeviceCatalogFiltersOptions = {
  externalSavedIds?: string[]
  initialSearch?: string
  onToggleSaved?: (id: string) => void | Promise<void>
}

export function useDeviceCatalogFilters(options: UseDeviceCatalogFiltersOptions = {}) {
  const { externalSavedIds, initialSearch, onToggleSaved } = options
  const { company, access } = useCurrentSession()
  const savedIdsStorageKey = getSavedIdsStorageKey(company.id, access.id)
  const [view, setViewState] = useState<DeviceCatalogView>('all')
  const [search, setSearch] = useState(initialSearch ?? '')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilters>({})
  const [savedIdsState, setSavedIdsState] = useState<string[]>(() =>
    loadSavedIds(savedIdsStorageKey)
  )
  const savedIds = externalSavedIds ?? savedIdsState
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    if (!externalSavedIds) {
      setSavedIdsState(loadSavedIds(savedIdsStorageKey))
    }
  }, [savedIdsStorageKey, externalSavedIds])

  const setView = useCallback((newView: DeviceCatalogView) => {
    setViewState(newView)
    if (newView !== 'category') {
      setSelectedCategory(null)
    }
  }, [])

  const setCategory = useCallback((categoryId: string | null) => {
    setSelectedCategory(categoryId)
    setViewState(categoryId ? 'category' : 'all')
  }, [])

  const setAttributeFilter = useCallback((key: string, values: string[]) => {
    setAttributeFilters((prev) => {
      if (values.length === 0) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: values }
    })
  }, [])

  const toggleSaved = useCallback(
    (id: string) => {
      if (onToggleSaved) {
        void onToggleSaved(id)
        return
      }

      setSavedIdsState((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        persistSavedIds(next, savedIdsStorageKey)
        return next
      })
    },
    [onToggleSaved, savedIdsStorageKey]
  )

  const isSaved = useCallback((id: string) => savedIds.includes(id), [savedIds])

  /**
   * Applies only view-level filters (view type + category).
   * Used to derive contextual filter options.
   */
  const applyViewFilter = useCallback(
    (items: DistributorProductVariant[]): DistributorProductVariant[] => {
      let filtered = items

      if (view === 'saved') {
        filtered = filtered.filter((item) => savedIds.includes(item.id))
      } else if (view === 'offers') {
        // No offer flag available in API yet — return all for now
      } else if (view === 'zero-touch') {
        filtered = filtered.filter((item) =>
          item.productAttributes.some(
            (attr) => attr.name.toLowerCase() === 'zero-touch' && attr.value.toLowerCase() === 'yes'
          )
        )
      } else if (view === 'category' && selectedCategory) {
        const allowedTypes = CATEGORY_TYPE_MAP[selectedCategory] ?? []
        filtered = filtered.filter((item) => {
          const typeName = item.product?.typeName?.toLowerCase().trim()
          return typeName ? allowedTypes.includes(typeName) : false
        })
      }

      return filtered
    },
    [view, selectedCategory, savedIds]
  )

  const extractFilterOptions = useCallback(
    (characteristicGroups: ProductCatalogCharacteristicGroup[]): FilterSection[] => {
      return characteristicGroups.map((group) => ({
        id: group.category,
        label: group.category,
        options: group.options.map((option) => ({
          value: option.name,
          label: `${option.name} (${option.quantity})`,
        })),
        selectedValues: attributeFilters[group.category] ?? [],
        onChange: (values: string[]) => setAttributeFilter(group.category, values),
      }))
    },
    [attributeFilters, setAttributeFilter]
  )

  const groupItems = useCallback(
    (items: DistributorProductVariant[]): ProductGroup[] => {
      if (view === 'all') {
        return [
          {
            id: 'all',
            title: i18n.t('it_management.device_catalog.views.all_devices_section'),
            description: i18n.t(
              'it_management.device_catalog.views.all_devices_section_description'
            ),
            items,
          },
        ]
      }

      if (view === 'offers') {
        return [
          {
            id: 'offers',
            title: i18n.t('it_management.device_catalog.views.current_offers'),
            description: i18n.t('it_management.device_catalog.views.offers_description'),
            items,
          },
        ]
      }

      // saved, zero-touch, category — single group
      const titleMap: Record<string, string> = {
        saved: i18n.t('it_management.device_catalog.views.saved'),
        'zero-touch': i18n.t('it_management.device_catalog.views.zero_touch'),
        category: selectedCategory ? getCategoryI18nLabel(selectedCategory) : '',
      }

      return [
        {
          id: view,
          title: titleMap[view] ?? '',
          items,
        },
      ]
    },
    [view, selectedCategory]
  )

  const state = useMemo(
    () => ({ view, search, selectedCategory, attributeFilters, savedIds }),
    [view, search, selectedCategory, attributeFilters, savedIds]
  )

  return {
    state,
    setView,
    setSearch,
    setCategory,
    setAttributeFilter,
    toggleSaved,
    isSaved,
    applyViewFilter,
    extractFilterOptions,
    groupItems,
  }
}
